import { Component, computed, inject, input, model, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of, timer } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { getCompleteUtcDateString } from '../../../date.utils';
import { NotifyService } from '../../../service/notify.service';
import { PosthogService } from '../../../service/posthog.service';
import { Branch, ReportExport } from '../../../types';
import { ReportPortalService } from '../../../service/report-portal.service';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-report-portal',
  templateUrl: './report-portal.component.html',
  styleUrls: ['./report-portal.component.scss'],
  standalone: false,
})
export class ReportPortalComponent implements OnInit {
  private reportPortalService = inject(ReportPortalService);
  private posthogService = inject(PosthogService);
  private notify = inject(NotifyService);

  readonly pageBranchIds = linkedQueryParam<number[]>('branch_ids', {
    parse: (value) => (value ? value.split(',').map((v) => parseInt(v, 10)) : []),
    stringify: (value) => value.join(','),
  });

  readonly pageDateFrom = linkedQueryParam<Date | undefined>('date_from', {
    parse: (value) => (value ? DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate() : undefined),
    stringify: (value) => (value ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') : undefined),
  });

  readonly pageDateTo = linkedQueryParam<Date | undefined>('date_to', {
    parse: (value) => (value ? DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate() : undefined),
    stringify: (value) => (value ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') : undefined),
  });

  public defaultBranchIds = model<number[]>([]);
  public currentBranches = model<Branch[]>([]);

  public branchId = input<number>();

  public isExporting = signal<boolean>(false);
  public reportExports = signal<ReportExport[]>([]);

  public isExportDisabled = computed(() => {
    const isReady = this.posthogService.isReady();
    if (!isReady) return false;
    return !!this.posthogService.isFeatureEnabled('disable_report_portal_export_button', false) || false;
  });

  public isExportReady = computed(() => this.dateFrom() !== undefined && this.dateTo() !== undefined);

  public dateFrom = signal<Date | undefined>(undefined);
  public dateTo = signal<Date | undefined>(undefined);

  constructor() {
    // Refresh report exports every 10 seconds
    timer(0, 10_000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.refreshReportExports();
      });
  }

  ngOnInit() {
    this.loadFromURLParams();
    this.refreshReportExports();
  }

  private loadFromURLParams() {
    const branchIds = this.pageBranchIds();

    const dateFrom = this.pageDateFrom() ?? DateTime.now().minus({ days: 7 }).toJSDate();
    const dateTo = this.pageDateTo() ?? new Date();

    this.dateFrom.set(dateFrom);
    this.dateTo.set(dateTo);

    if (branchIds.length > 0) {
      this.defaultBranchIds.set(branchIds);
    }
  }

  updateBranchData() {
    this.pageBranchIds.set(this.currentBranches().map((b) => b.id));
    this.pageDateFrom.set(this.dateFrom());
    this.pageDateTo.set(this.dateTo());
  }

  export(reportExportId?: string) {
    this.posthogService.capture('start_report_export');
    this.isExporting.set(true);
    this.reportPortalService
      .exportReportPortalData(reportExportId, this.dateFrom(), this.dateTo())
      .pipe(
        catchError(() => of(null)),
        tap(() => {
          this.notify.confirm('Report Portal', 'Your export is now being generated, and it will be sent to your email in a few minutes.');
        }),
        finalize(() => {
          this.isExporting.set(false);
          this.refreshReportExports();
        }),
      )
      .subscribe();
  }

  refreshReportExports() {
    this.reportPortalService.getReportExports().subscribe(
      (reportExports) => {
        this.reportExports.set(
          reportExports.map((reportExport) => {
            return {
              ...reportExport,
              created_at: getCompleteUtcDateString(reportExport.created_at),
            };
          }),
        );
      },
      () => {},
    );
  }

  downloadReportExport(reportExport: ReportExport) {
    this.posthogService.capture('download_report_export', { id: reportExport.id });
    // Reference: https://stackoverflow.com/a/37673039/24230721
    const a = document.createElement('a');
    a.href = reportExport.download_url;
    a.target = '_blank';
    a.download = reportExport.name;
    a.click();

    this.reportPortalService.markReportExportDownloaded(reportExport.id).subscribe(() => this.refreshReportExports());
  }
}
